import type { Question } from '../types';

// ---------------------------------------------------------------------------
// ACORD 125 — Universal base questions (always shown, no conditions)
// ---------------------------------------------------------------------------

export const acordBaseQuestions: Question[] = [
  // -- Business Information --------------------------------------------------
  {
    id: 'biz_name',
    text: 'What is the legal name of the business?',
    subtext: 'As it appears on legal documents and tax filings.',
    section: 'business_info',
    sort_order: 10,
    field_type: 'text',
    placeholder: 'Acme Construction LLC',
    required: true,
    field_path: 'business.legal_name',
    conditions: [],
    acord_field: 'ApplicantName',
    tags: ['acord125', 'core'],
  },
  {
    id: 'biz_dba',
    text: 'Does the business operate under a DBA (doing business as)?',
    subtext: 'Leave blank if same as legal name.',
    section: 'business_info',
    sort_order: 20,
    field_type: 'text',
    placeholder: 'Acme Builders',
    required: false,
    field_path: 'business.dba',
    conditions: [],
    acord_field: 'DBA',
    tags: ['acord125'],
  },
  {
    id: 'biz_fein',
    text: 'Federal Employer Identification Number (FEIN)',
    subtext: 'Also known as your EIN or Tax ID.',
    section: 'business_info',
    sort_order: 30,
    field_type: 'text',
    placeholder: 'XX-XXXXXXX',
    required: true,
    validation: { pattern: '^\\d{2}-?\\d{7}$' },
    field_path: 'business.fein',
    conditions: [],
    acord_field: 'FEIN',
    tags: ['acord125', 'core'],
  },
  {
    id: 'biz_entity_type',
    text: 'What type of business entity is this?',
    section: 'business_info',
    sort_order: 40,
    field_type: 'select',
    options: [
      { label: 'Sole Proprietor', value: 'sole_proprietor' },
      { label: 'Partnership', value: 'partnership' },
      { label: 'Corporation', value: 'corporation' },
      { label: 'S-Corporation', value: 's_corporation' },
      { label: 'LLC', value: 'llc' },
      { label: 'Joint Venture', value: 'joint_venture' },
      { label: 'Trust', value: 'trust' },
      { label: 'Non-Profit', value: 'non_profit' },
      { label: 'Municipality / Government', value: 'government' },
      { label: 'Other', value: 'other' },
    ],
    required: true,
    field_path: 'business.entity_type',
    conditions: [],
    acord_field: 'EntityType',
    tags: ['acord125', 'core'],
  },
  {
    id: 'biz_years_in_business',
    text: 'How many years has the business been in operation?',
    section: 'business_info',
    sort_order: 50,
    field_type: 'number',
    placeholder: '5',
    required: true,
    validation: { min: 0, max: 200 },
    field_path: 'business.years_in_business',
    conditions: [],
    acord_field: 'YearsInBusiness',
    tags: ['acord125', 'core'],
  },
  {
    id: 'biz_website',
    text: 'Business website',
    section: 'business_info',
    sort_order: 60,
    field_type: 'text',
    placeholder: 'https://www.example.com',
    required: false,
    field_path: 'business.website',
    conditions: [],
    tags: ['acord125'],
  },

  // -- Business Address ------------------------------------------------------
  {
    id: 'biz_address_street',
    text: 'Business street address',
    section: 'business_info',
    group: 'address',
    sort_order: 70,
    field_type: 'text',
    placeholder: '123 Main Street',
    required: true,
    field_path: 'business.address.street',
    conditions: [],
    acord_field: 'MailAddr1',
    tags: ['acord125', 'core'],
  },
  {
    id: 'biz_address_city',
    text: 'City',
    section: 'business_info',
    group: 'address',
    sort_order: 80,
    field_type: 'text',
    placeholder: 'Springfield',
    required: true,
    field_path: 'business.address.city',
    conditions: [],
    acord_field: 'MailCity',
    tags: ['acord125', 'core'],
  },
  {
    id: 'biz_address_state',
    text: 'State',
    section: 'business_info',
    group: 'address',
    sort_order: 90,
    field_type: 'state_select',
    required: true,
    field_path: 'business.address.state',
    conditions: [],
    acord_field: 'MailState',
    tags: ['acord125', 'core'],
  },
  {
    id: 'biz_address_zip',
    text: 'ZIP code',
    section: 'business_info',
    group: 'address',
    sort_order: 100,
    field_type: 'text',
    placeholder: '62704',
    required: true,
    validation: { pattern: '^\\d{5}(-\\d{4})?$' },
    field_path: 'business.address.zip',
    conditions: [],
    acord_field: 'MailZip',
    tags: ['acord125', 'core'],
  },

  // -- Business Description --------------------------------------------------
  {
    id: 'biz_description',
    text: 'Describe the business operations',
    subtext:
      'Brief description of what the business does, the products or services it provides, and any specializations.',
    section: 'business_info',
    sort_order: 110,
    field_type: 'textarea',
    placeholder: 'Residential and commercial HVAC installation and repair services...',
    required: true,
    field_path: 'business.description',
    conditions: [],
    acord_field: 'DescOps',
    tags: ['acord125', 'core'],
  },

  // -- Contact Information ---------------------------------------------------
  {
    id: 'contact_name',
    text: 'Primary contact name',
    section: 'contact',
    sort_order: 10,
    field_type: 'text',
    placeholder: 'Jane Smith',
    required: true,
    field_path: 'contact.name',
    conditions: [],
    acord_field: 'ContactName',
    tags: ['acord125', 'core'],
  },
  {
    id: 'contact_title',
    text: 'Contact title / role',
    section: 'contact',
    sort_order: 20,
    field_type: 'text',
    placeholder: 'Owner',
    required: false,
    field_path: 'contact.title',
    conditions: [],
    acord_field: 'ContactTitle',
    tags: ['acord125'],
  },
  {
    id: 'contact_email',
    text: 'Contact email address',
    section: 'contact',
    sort_order: 30,
    field_type: 'email',
    placeholder: 'jane@example.com',
    required: true,
    field_path: 'contact.email',
    conditions: [],
    acord_field: 'ContactEmail',
    tags: ['acord125', 'core'],
  },
  {
    id: 'contact_phone',
    text: 'Contact phone number',
    section: 'contact',
    sort_order: 40,
    field_type: 'phone',
    placeholder: '(555) 123-4567',
    required: true,
    field_path: 'contact.phone',
    conditions: [],
    acord_field: 'ContactPhone',
    tags: ['acord125', 'core'],
  },
  {
    id: 'contact_phone_ext',
    text: 'Phone extension',
    section: 'contact',
    sort_order: 50,
    field_type: 'text',
    placeholder: '101',
    required: false,
    field_path: 'contact.phone_ext',
    conditions: [],
    tags: ['acord125'],
  },
  {
    id: 'contact_best_time',
    text: 'Best time to reach this contact?',
    section: 'contact',
    sort_order: 60,
    field_type: 'select',
    options: [
      { label: 'Morning (8am–12pm)', value: 'morning' },
      { label: 'Afternoon (12pm–5pm)', value: 'afternoon' },
      { label: 'Evening (5pm–8pm)', value: 'evening' },
      { label: 'Anytime', value: 'anytime' },
    ],
    required: false,
    field_path: 'contact.best_time',
    conditions: [],
    tags: ['acord125'],
  },
  {
    id: 'contact_preferred_method',
    text: 'Preferred contact method?',
    section: 'contact',
    sort_order: 70,
    field_type: 'select',
    options: [
      { label: 'Email', value: 'email' },
      { label: 'Phone', value: 'phone' },
      { label: 'Text / SMS', value: 'sms' },
      { label: 'No preference', value: 'any' },
    ],
    required: false,
    field_path: 'contact.preferred_method',
    conditions: [],
    tags: ['acord125'],
  },
];
